import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { isFeatureEnabled, ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useAsync } from 'react-use';
import { useSnapshot } from 'valtio';
import { getProjectSummary } from '~/api/projects2';
import InfoTooltip from '~/components2/InfoTooltip';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import store from '../store';
import Section from './Section';
import UpgradeButton from './UpgradeButton';

export default function UtilitiesComponent() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { billingTier } = useSnapshot(store) as typeof store;
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const civilSearchEnabled = isFeatureEnabled(
    ProjectFeature.CivilSearch,
    billingTier,
    currentOrganization?.subscription,
  );

  const res = useAsync(async () => {
    return await getProjectSummary<{
      totalAgencies: number;
      assetTypes: number;
      availableDigitally: number;
      availableForRequest: number;
    }>(projectId, 'utilities');
  }, [projectId]);

  const pill = (title: string, value: ReactNode, tooltip?: ReactNode) => (
    <div key={title} className="flex flex-col rounded-lg border border-grey-30 bg-secondary-light/30 p-4 text-left">
      <div className="text-2xl font-medium">{value}</div>
      <div className="flex space-x-1 text-base">
        <span>{title}</span>
        {tooltip && <InfoTooltip text={tooltip} label={`About ${title}`} />}
      </div>
    </div>
  );

  if (res.error) {
    console.error(res.error);
    return null;
  }

  if (!civilSearchEnabled) {
    return (
      <Section icon="⚡️" title="Utilities">
        <div data-testid="civilsearch-upgrade-state" className="space-y-2">
          <div className="text-sm">
            Upgrade to see the utility agencies operating in your project area and unlock CivilGrid's premium
            CivilSearch service — we request maps and as-builts from agencies on your behalf and manage the whole
            process end to end. In this section you'll be able to explore:
            <div className="my-2 grid grid-cols-2">
              <div>Utility Agencies</div>
              <div>Asset Types</div>
              <div>Digitally Available Data</div>
              <div>Live Map Viewing</div>
              <div>CAD / GIS Downloads</div>
              <div>Agency Maps &amp; As-Builts</div>
              <div>Managed CivilSearch Requests</div>
              <div>Utility Coverage Overview</div>
            </div>
          </div>
          <UpgradeButton>View Utilities</UpgradeButton>
        </div>
      </Section>
    );
  }

  return (
    <Section icon="⚡️" title="Utilities">
      {res.loading ? (
        <SkeletonSpinnerCentered />
      ) : (
        <div className="flex flex-col gap-2 text-sm">
          <span>Here is a summary of utility agencies that operate in your project area.</span>
          <div className="grid grid-cols-2 gap-2">
            {pill(
              'Utility Agencies',
              res.value?.totalAgencies,
              'List of unique utility agencies within the project boundaries. Note that some entities may appear multiple times in the list due to differences in legal or operating names, or slight differences in how they are reported in various databases. Certain datasets contain both “Owner” and “Operator” fields - right-click to view Layer Attributes.',
            )}
            {pill(
              'Asset Types',
              res.value?.assetTypes,
              <div>
                <div>Number of unique types of utility assets.</div>
              </div>,
            )}
            {pill(
              'Available Digitally',
              res.value?.availableDigitally,
              'Number of agencies with digital data on CivilGrid, meaning you can view it in the live map or download to CAD/GIS.',
            )}
            {pill(
              'Available for Request',
              res.value?.availableForRequest,
              'Number of agencies where you can request Maps and as-builts through our premium CivilSearch service. CivilGrid will handle utility requests on your behalf, managing the entire process from start to finish.',
            )}
          </div>
          <UpgradeButton>View Detailed Utility Info</UpgradeButton>
        </div>
      )}
    </Section>
  );
}
